import React, { useCallback, useEffect, useState } from 'react';
import { CouponReturn } from '../clients/rust-api-client';
import { billingService } from '../clients/ApiClients';
import { Button } from '../common/Catalyst/button';
import { Subheading } from '../common/Catalyst/heading';
import { Text } from '../common/Catalyst/text';
import LoadingSpinner from '../LoadingSpinner';
import ShowAlert from '../ShowAlert';
import {
  billingErrorMessage,
  compareMoney,
  formatBillingDate,
  formatMoney,
  savePdfResponse,
} from '../utils/billingHelpers';

interface Props {
  homeId: string;
}

/**
 * The scanline's amount field is ten digits — `$99,999,999.98` is the
 * most it can carry. Widened from six in phase 7, because a special
 * assessment cleared `$9,999.99` easily and a delinquent account
 * reached it just by aging.
 */
const SCANLINE_MAX = '99999999.98';

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex justify-between gap-4 py-2 border-b border-zinc-100 dark:border-zinc-800">
    <span className="text-sm text-zinc-500 dark:text-zinc-400">{label}</span>
    <span className="text-sm text-zinc-900 dark:text-white">{value}</span>
  </div>
);

/**
 * The payment coupon a homeowner tears off and posts back.
 *
 * **The scanline is not validated.** Its format is a guess from a
 * single Columbia sample and the trailing check digit is omitted
 * entirely, so it is shown for reference rather than presented as
 * something a lockbox will certainly read. That caveat belongs on this
 * screen and not on the printed coupon.
 */
const CouponPanel: React.FC<Props> = ({ homeId }) => {
  const [coupon, setCoupon] = useState<CouponReturn | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await billingService.getCoupon({ homeId });
      setCoupon(data);
    } catch (err) {
      console.error('Error loading coupon:', err);
      setError(billingErrorMessage(err, "Couldn't load the coupon."));
    } finally {
      setLoading(false);
    }
  }, [homeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const download = async (): Promise<void> => {
    try {
      setDownloading(true);
      const response = await billingService.couponPdf(
        { homeId },
        { responseType: 'blob' as const }
      );
      savePdfResponse(response, `coupon-${homeId}.pdf`);
    } catch (err) {
      console.error('Error downloading coupon:', err);
      setError(billingErrorMessage(err, "Couldn't download the coupon."));
    } finally {
      setDownloading(false);
    }
  };

  if (loading && !coupon) return <LoadingSpinner />;

  const overScanline =
    coupon != null && compareMoney(coupon.amount_due, SCANLINE_MAX) > 0;

  return (
    <div>
      {error && (
        <div className="mb-4">
          <ShowAlert
            alertType="error"
            alertTitle="Coupon"
            alertMessage={error}
            isVisible={!!error}
            onClose={() => setError(null)}
            timeout={12000}
            isInline
          />
        </div>
      )}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Subheading>Payment coupon</Subheading>
          <Text className="mt-1">
            What the homeowner needs in order to pay, and what the lockbox
            reads.
          </Text>
        </div>
        <Button color="sky" onClick={download} disabled={downloading}>
          {downloading ? 'Preparing…' : 'Download PDF'}
        </Button>
      </div>

      {coupon && (
        <>
          <div className="mt-6 max-w-xl">
            <Row label="Payable to" value={coupon.payable_to} />
            <Row
              label="Account number"
              value={<span className="font-mono">{coupon.account_number}</span>}
            />
            <Row label="Payer" value={coupon.payer_name} />
            <Row
              label="This period"
              value={
                coupon.current_period_amount
                  ? formatMoney(coupon.current_period_amount)
                  : 'No schedule in force'
              }
            />
            <Row
              label="Amount due"
              value={<strong>{formatMoney(coupon.amount_due)}</strong>}
            />
            <Row
              label="Due"
              value={coupon.due_date ? formatBillingDate(coupon.due_date) : '—'}
            />
            <Row
              label="Late after"
              value={
                coupon.grace_date ? formatBillingDate(coupon.grace_date) : '—'
              }
            />
          </div>

          <Subheading level={4} className="mt-8">
            Scanline
          </Subheading>
          <pre className="mt-2 overflow-x-auto rounded-md border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-900 p-3 font-mono text-sm">
            {coupon.scanline}
          </pre>

          {overScanline && (
            <div className="mt-3 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-4">
              <Text>
                <strong>
                  {formatMoney(coupon.amount_due)} will not fit the
                  scanline&rsquo;s amount field.
                </strong>{' '}
                It carries ten digits, so {formatMoney(SCANLINE_MAX)} is the
                most it can express. The full amount is emitted rather than
                truncated, but a lockbox reading it may not accept the line —
                take payment another way.
              </Text>
            </div>
          )}

          <Text className="mt-3 text-xs">
            The scanline format is inferred from a single sample from the bank
            and the trailing check digit is omitted, so it has not been
            validated end to end. It is printed on the coupon for reference; the
            account number above is the part that is known to be right.
          </Text>
        </>
      )}
    </div>
  );
};

export default CouponPanel;
